import { FC, ReactNode } from 'react';
import { Heading, Icon, Paragraph } from '@centrix/shared-ui';
import {
  documentIcon,
  completionGaugeIcon,
  facebookIcon,
  xTwitterIcon,
  linkedinIcon,
  instagramIcon,
} from '@centrix/shared-ui/src/icons';
import { SOCIAL_LINKS } from '@centrix/centrix-brand';

import logoImage from '@/assets/logo.png';
import loginImage from '@/assets/login.png';
import loginDotsImage from '@/assets/login-dots.png';

const CompletionRateCard: FC = () => {
  return (
    <div className="relative bg-white rounded-lg shadow-elevation-soft w-46.5 h-36.5 border border-slate-200 overflow-hidden">
      {/* Half-gauge arc (Figma node 1262:128459/60). SVG aspect 200x120, rendered 130 wide */}
      <img
        src={completionGaugeIcon}
        alt=""
        aria-hidden="true"
        className="absolute left-7 top-3.5 w-32.5 h-auto pointer-events-none"
      />
      {/* 87.6% label (Figma 1262:128457 at 67,61) */}
      <Paragraph className="absolute left-16.75 top-15.25 w-13.25 text-lg font-semibold text-black leading-[1.4] text-center">
        87.6%
      </Paragraph>
      {/* Completion rate caption (Figma 1262:128458 at 10,89) */}
      <Paragraph className="absolute left-[10.5px] top-22.25 w-41.25 text-sm text-black leading-[1.4] text-center">
        Completion rate of our courses
      </Paragraph>
    </div>
  );
};

const CoursesSoldCard: FC = () => {
  return (
    <div className="relative bg-white rounded-lg shadow-elevation-soft w-59 h-30.25 border border-slate-200 overflow-hidden">
      {/* Icon bubble (Figma node 1262:128452 at 16,16, 48x48) */}
      <div className="absolute left-4 top-4 w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
        <Icon src={documentIcon} size="lg" className="text-primary" />
      </div>
      {/* "Number of courses sold" (Figma 1262:128454 at 72,18, 154x44) */}
      <Paragraph className="absolute left-18 top-4.5 w-38.5 text-base font-semibold text-slate-500 leading-[1.4]">
        Number of courses sold
      </Paragraph>
      {/* "100,000+" (Figma 1262:128455 at 72,66) */}
      <Paragraph className="absolute left-18 top-16.5 text-[28px] font-semibold text-black leading-[1.4]">
        100,000+
      </Paragraph>
    </div>
  );
};

const SOCIAL_ICONS: Record<string, string> = {
  Facebook: facebookIcon,
  'X (Twitter)': xTwitterIcon,
  LinkedIn: linkedinIcon,
  Instagram: instagramIcon,
};

const socialIconStyle = (src: string) =>
  ({
    backgroundColor: 'currentColor',
    maskImage: `url("${src}")`,
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    maskSize: 'contain',
    WebkitMaskImage: `url("${src}")`,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    WebkitMaskSize: 'contain',
  }) as const;

/**
 * Branded social-media footer (FROZEN Index row 5). Rendered at the foot of the
 * auth shell's form column so the links are reachable on every public auth page.
 * Destinations come from the brand package (single source of truth) and open in
 * a new tab with `rel="noopener"`.
 */
const SocialFooter: FC = () => (
  <footer className="mt-10 flex flex-col items-center gap-3">
    <Paragraph className="text-sm text-gray-500">Follow us</Paragraph>
    <div className="flex items-center gap-5">
      {SOCIAL_LINKS.map(({ label, href }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="text-gray-600 opacity-60 hover:opacity-100 transition-opacity"
        >
          <span
            aria-hidden="true"
            className="inline-block w-5 h-5"
            style={socialIconStyle(SOCIAL_ICONS[label])}
          />
        </a>
      ))}
    </div>
  </footer>
);

interface AuthLayoutProps {
  /** The form content rendered in the right column, below the logo. */
  children: ReactNode;
}

/**
 * Two-column authentication shell shared by the Login, Forgot Password and
 * Reset Password screens (Figma 2959:35487 / 2959:35587). The left hero panel
 * and the logo + centered form column are identical across all three, so they
 * live here once instead of being duplicated per page.
 */
export const AuthLayout: FC<AuthLayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Hero Section */}
      <div className="lg:w-[59.55%] bg-brand-panel flex flex-col items-center justify-center relative overflow-hidden py-12 px-8">
        {/* Heading */}
        <div className="text-center mb-25.5 z-10">
          <Heading
            as="h1"
            className="text-5xl font-medium text-black leading-14 tracking-[-0.96px]"
          >
            Welcome to Learnovate360!
          </Heading>
          <Paragraph className="text-[28px] text-black leading-10 tracking-[-0.56px] mt-5.25">
            Your Ultimate Learning Hub!
          </Paragraph>
        </div>

        {/* Image with decorative frames and floating cards */}
        <div className="relative z-10 flex items-center justify-center">
          {/* Dotted grid decoration — rendered first so the overlap sits behind the image */}
          <img
            src={loginDotsImage}
            alt=""
            aria-hidden="true"
            className="absolute -left-14 top-7.25 w-29.75 h-29.5 pointer-events-none select-none"
          />

          {/* Outer purple border frame (offset behind) */}
          <div className="absolute top-10.5 left-9.25 w-82.5 h-110 rounded-[26.4px] border-[4.4px] border-brand-outline-soft" />

          {/* Inner image container */}
          <div className="relative w-82.5 h-110 rounded-[26.4px] overflow-hidden bg-blue-300">
            <img
              src={loginImage}
              alt="Learning platform illustration"
              className="w-full h-full object-cover object-right"
            />
          </div>

          {/* Completion Rate Card - top right */}
          <div className="absolute -top-5.25 -right-28 z-20">
            <CompletionRateCard />
          </div>

          {/* Courses Sold Card - bottom left */}
          <div className="absolute -bottom-6.5 -left-35.5 z-20">
            <CoursesSoldCard />
          </div>
        </div>
      </div>

      {/* Right Side - Form column */}
      <div className="lg:w-[40.45%] bg-white flex flex-col items-center justify-center py-12 px-8">
        <div className="w-full max-w-107">
          {/* Logo */}
          <div className="flex justify-center mb-9.5">
            <img src={logoImage} alt="Learnovate360" className="w-61 h-28.75" />
          </div>

          {children}

          <SocialFooter />
        </div>
      </div>
    </div>
  );
};
